import { ChevronRight } from "lucide-react"
import {
  createContext,
  Fragment,
  type ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from "react"
import { createPortal } from "react-dom"
import type { Scope } from "@/lib/connection"
import { useT } from "@/lib/i18n"
import { EnvDot } from "./scope-switcher"
import { ShellNavButton, ShellRailButton } from "./shell-nav"

// Outside the shell, a page renders its own header. Inside it, every page shares the site bar.
const SiteHeaderContext = createContext<HTMLElement | null | undefined>(undefined)
const RAIL = "(min-width: 48rem)"

function subscribeRail(onChange: () => void) {
  const rail = window.matchMedia(RAIL)
  rail.addEventListener("change", onChange)
  return () => rail.removeEventListener("change", onChange)
}

export function useDesktopRail() {
  return useSyncExternalStore(
    subscribeRail,
    () => window.matchMedia(RAIL).matches,
    () => false,
  )
}

/** The Console site header: one location bar above the slab, even on loading and refusal. */
export function ShellPageHeader({ scope, children }: { scope: Scope; children: ReactNode }) {
  const desktop = useDesktopRail()
  const [slot, setSlot] = useState<HTMLDivElement | null>(null)
  return (
    <SiteHeaderContext.Provider value={slot}>
      {/* biome-ignore lint/a11y/noInteractiveElementToNoninteractiveRole: The site header is the single banner outside main. */}
      <header
        role="banner"
        className="group flex h-12 shrink-0 items-center gap-2 border-b border-border px-3 md:gap-3 md:px-4"
      >
        {desktop ? <ShellRailButton /> : <ShellNavButton />}
        <span aria-hidden className="h-4 border-s border-border" />
        <div ref={setSlot} className="contents" />
        {/* The fallback is visible only while no page has breadcrumb content in the slot. */}
        <div className="flex min-w-0 flex-1 items-center gap-2 group-has-[nav]:hidden">
          <span className="min-w-0 flex-1 truncate text-sm font-medium">{scope.orgId}</span>
          <EnvDot environmentId={scope.environmentId} />
          <span className="truncate text-xs text-muted-foreground">{scope.environmentId}</span>
        </div>
      </header>
      {children}
    </SiteHeaderContext.Provider>
  )
}

/**
 * The Console site-header breadcrumb and live page actions. The shell owns the bar;
 * Earlier crumbs are router links the caller builds (typed routes); the last
 * one is the current page.
 */
export function PageHeader({ crumbs, actions }: { crumbs: readonly ReactNode[]; actions?: ReactNode }) {
  const t = useT()
  const siteSlot = useContext(SiteHeaderContext)
  const current = useRef<HTMLSpanElement>(null)
  useEffect(() => {
    const title = current.current?.textContent
    if (title) document.title = `${title} · ${t("app.name")}`
  }, [crumbs, siteSlot, t])
  const content = (
    <>
      <nav aria-label={t("shell.breadcrumb")} className="flex min-w-0 items-center gap-1 text-sm">
        {crumbs.map((c, i) => {
          const last = i === crumbs.length - 1
          return (
            <Fragment key={i}>
              {/* On phones only the current page fits; the drawer and the page itself lead back up. */}
              {i > 0 ? (
                <ChevronRight
                  className="rtl-mirror hidden size-3.5 shrink-0 text-subtle-foreground md:block"
                  aria-hidden
                />
              ) : null}
              <span
                ref={last ? current : undefined}
                aria-current={last ? "page" : undefined}
                className={
                  last
                    ? "truncate font-medium text-foreground"
                    : "hidden truncate text-muted-foreground md:inline [&_a]:transition-colors [&_a:hover]:text-foreground"
                }
              >
                {c}
              </span>
            </Fragment>
          )
        })}
      </nav>
      {actions ? (
        <div className="ms-auto flex items-center gap-2 max-md:min-w-0 max-md:max-w-[70%] max-md:overflow-x-auto max-md:[&>span]:truncate">
          {actions}
        </div>
      ) : null}
    </>
  )
  if (siteSlot !== undefined) return siteSlot ? createPortal(content, siteSlot) : null
  return (
    <header className="flex h-12 shrink-0 items-center gap-2 border-b border-border px-3 md:gap-3 md:px-4">
      {content}
    </header>
  )
}
